import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { calculateMegapixelsImageCost } from "../helpers";
import { attachment } from "../shared";
import { defineModel } from "../types";

const GPT_IMAGE_ASPECT_RATIOS = [
  { label: "1:1", value: "1:1" },
  { label: "16:9", value: "16:9" },
  { label: "9:16", value: "9:16" },
] as const;

const GPT_IMAGE_QUALITY_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
] as const;

const GPT_IMAGE_BACKGROUND_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "Transparent", value: "transparent" },
  { label: "Opaque", value: "opaque" },
] as const;

type GptImageAspectRatio = (typeof GPT_IMAGE_ASPECT_RATIOS)[number]["value"];

const DIMENSION_MAP: Record<
  GptImageAspectRatio,
  { width: number; height: number }
> = {
  "1:1": { width: 1024, height: 1024 },
  "16:9": { width: 1536, height: 1024 },
  "9:16": { width: 1024, height: 1536 },
};

type GptImageQuality = (typeof GPT_IMAGE_QUALITY_OPTIONS)[number]["value"];

// Runware pricing per megapixel for GPT Image (OpenAI gpt-image model)
// https://docs.runware.ai/en/image-inference/models
const COST_PER_MEGAPIXEL: Record<GptImageQuality, number> = {
  low: 0.009,
  medium: 0.034,
  high: 0.13,
  auto: 0.13, // Conservative: use high price to ensure hold covers actual charge
};

const gptImage15Params = z
  .object({
    aspectRatio: z.enum(["1:1", "16:9", "9:16"] as const).default("9:16"),
    quality: z.enum(["auto", "high", "medium", "low"] as const).default("auto"),
    background: z
      .enum(["auto", "transparent", "opaque"] as const)
      .default("auto"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

// Supports up to 16 reference images via referenceImages
const ATTACHMENT_SLOTS = Array.from({ length: 16 }, (_, index) => ({
  type: "image" as const,
  allowedFormats: ["image/jpeg", "image/png", "image/webp"],
  label: `Image ${index + 1}`,
  slot: `reference_image_${index + 1}`,
  group: "reference",
  required: false,
}));

export const gptImage15 = defineModel({
  key: "gpt-image-1-5",
  stepType: "image",
  spec: {
    label: "GPT Image 1.5",
    description:
      "OpenAI's flagship image model with fast generation, precise edits, and excellent text rendering",
    icon: "openai.svg",
    tags: ["image-gen", "image-edit"],
    features: {
      aspectRatio: true,
      quality: true,
      background: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: GPT_IMAGE_ASPECT_RATIOS },
      quality: { items: GPT_IMAGE_QUALITY_OPTIONS },
      background: { items: GPT_IMAGE_BACKGROUND_OPTIONS },
      attachments: ATTACHMENT_SLOTS,
    },
    params: gptImage15Params,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/gpt-image-1.5",
    entries: [
      { unit: "per_image", unitPriceMicrodollars: 14_156, variant: "Low" },
      { unit: "per_image", unitPriceMicrodollars: 47_186, variant: "Medium" },
      { unit: "per_image", unitPriceMicrodollars: 209_191, variant: "High" },
    ],
  },
  providers: [
    {
      provider: "runware",
      modelId: "openai:4@1",
      estimateCost(params) {
        const aspectRatio = params.aspectRatio ?? "9:16";
        const quality = params.quality ?? "auto";
        const dimensions = DIMENSION_MAP[aspectRatio];
        const costPerMegapixel = COST_PER_MEGAPIXEL[quality];
        const baseCost = calculateMegapixelsImageCost(
          dimensions.width,
          dimensions.height,
          costPerMegapixel,
        );
        return Math.ceil(baseCost * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params) {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const referenceImages =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        const dimensions = DIMENSION_MAP[params.aspectRatio];

        // Use PNG format when transparent background is requested (JPG doesn't support transparency)
        const outputFormat =
          params.background === "transparent" ? "PNG" : undefined;

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          width: dimensions.width,
          height: dimensions.height,
          ...(outputFormat ? { outputFormat } : {}),
          ...(referenceImages.length ? { referenceImages } : {}),
          providerSettings: {
            openai: {
              quality: params.quality,
              background: params.background,
            },
          },
        };
      },
    },
  ],
});
